import {
  AlignItems,
  Body1,
  Box,
  Breakpoint,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  Heading1,
  Overflow,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { HeadlineStyle, HeroCardData } from '@library/api-customer-engagement-experience'

import collageDesktop from './assets/collage-desktop.webp'
import collageMobile from './assets/collage-mobile.webp'
import collageTablet from './assets/collage-tablet.webp'

type HeroCardProps = {
  data: HeroCardData
  onActionClick: (intent: string) => void
}

// Sizes taken from the Figma iPad and desktop frames.
const CARD_SHADOW = '0 4px 8px rgba(33, 41, 48, 0.05), 0 2px 2px rgba(33, 41, 48, 0.06)'
const DESIGN_TEXT_COLUMN_MAX_WIDTH = '502px'
const DESIGN_CTA_WIDTH = 368

// Starts at the Figma offset (400px iPad, 378px from 768px); narrower cards crop its right side.
const CollageFrame = withCitrusTheme(Box, () => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: '400px',
  overflow: 'hidden',
  pointerEvents: 'none',
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    left: '378px',
  },
}))

// Figma: 8px radius and a 16/24 label, 18/28 from 768px (the layout switches at 640px).
const CtaButton = withCitrusTheme(Button, () => ({
  borderRadius: '8px',
  fontSize: '16px',
  lineHeight: '24px',
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    fontSize: '18px',
    lineHeight: '28px',
  },
}))

const PositiveText = withCitrusTheme('span', (theme) => ({
  color: theme.color(Color.Success_900),
  fontWeight: 700,
}))

type HeadlineSegment = { text: string; positive: boolean }

const splitHeadline = (headline: string, styles: HeadlineStyle[]): HeadlineSegment[] =>
  styles
    .filter((style) => style.type === 'positive' && style.text)
    .reduce<HeadlineSegment[]>(
      (segments, { text }) => {
        let hasMatched = false

        return segments.flatMap((segment) => {
          const index = hasMatched || segment.positive ? -1 : segment.text.indexOf(text)
          if (index === -1) {
            return [segment]
          }

          hasMatched = true
          const before = segment.text.slice(0, index)
          const after = segment.text.slice(index + text.length)

          return [
            ...(before ? [{ text: before, positive: false }] : []),
            { text, positive: true },
            ...(after ? [{ text: after, positive: false }] : []),
          ]
        })
      },
      [{ text: headline, positive: false }],
    )

// Emphasises the substrings the xAPI marks in `headline_styles`, without it sending HTML.
const StyledHeadline = ({
  headline,
  styles = [],
}: {
  headline: string
  styles?: HeadlineStyle[]
}) => {
  let offset = 0

  return (
    <>
      {splitHeadline(headline, styles).map((segment) => {
        const key = offset
        offset += segment.text.length

        return segment.positive ? (
          <PositiveText key={key}>{segment.text}</PositiveText>
        ) : (
          segment.text
        )
      })}
    </>
  )
}

const CollageImage = withCitrusTheme('img', () => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: 'auto',
  pointerEvents: 'none',
  userSelect: 'none',
}))

export const HeroCard = ({ data, onActionClick }: HeroCardProps) => (
  <Box
    bg={Color.White}
    boxShadow={CARD_SHADOW}
    data-testid="hero-card"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={[3, 0]}
    overflowX={Overflow.Hidden}
    overflowY={Overflow.Hidden}
    position={DisplayPosition.Relative}
    style={{ borderRadius: 8 }}
  >
    <Box display={[Display.Flex, Display.None]} style={{ justifyContent: 'center', paddingTop: 8 }}>
      <img
        alt=""
        aria-hidden="true"
        data-testid="hero-card-collage"
        src={collageMobile}
        style={{ width: 'clamp(326px, 70%, 460px)', height: 'auto' }}
      />
    </Box>

    <CollageFrame display={[Display.None, Display.Block]}>
      <picture>
        <source media={`(min-width: ${Breakpoint.Medium})`} srcSet={collageDesktop} />
        <CollageImage
          alt=""
          aria-hidden="true"
          data-testid="hero-card-collage"
          src={collageTablet}
        />
      </picture>
    </CollageFrame>

    <Box
      alignItems={AlignItems.FlexStart}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      maxWidth={['100%', DESIGN_TEXT_COLUMN_MAX_WIDTH]}
      paddingBottom={[3, 8]}
      paddingH={[4, 8]}
      paddingV={[0, 8]}
      position={DisplayPosition.Relative}
    >
      <Heading1 color={Color.ColdGrey_800} size={[3, 10]}>
        <StyledHeadline headline={data.headline} styles={data.headline_styles} />
      </Heading1>
      <Body1 color={[Color.ColdGrey_800, Color.ColdGrey_600]} size={[2, 6]}>
        {data.body}
      </Body1>
      <CtaButton
        colorVariant={ButtonColorVariant.Primary}
        data-testid="hero-card-cta"
        height={48}
        onClick={() => onActionClick(data.cta.intent)}
        type="button"
        width={['100%', DESIGN_CTA_WIDTH]}
      >
        {data.cta.title}
      </CtaButton>
    </Box>
  </Box>
)
